/**
 * Maps World Cup knockout fixtures (from useWorldCupMatchesQuery) into the
 * source-agnostic `KnockoutBracketModel` the shared bracket renders.
 *
 * The knockout fixtures already carry resolved teams / scores / kickoff once the
 * draw is made; before then a side with no country code is treated as a TBD
 * placeholder. The third-place match is pulled out and rendered under the Final.
 */

import type {
  BracketMatch,
  BracketRound,
  BracketTeamSlot,
  KnockoutBracketModel,
} from '../../knockout/types/knockoutBracket.types'
import type {
  TournamentStageKey,
  WorldCupMatch,
} from '../types/worldcup.types'
import { formatKickoffClock } from './worldcup.helper'
import { orderKnockoutRounds } from './knockoutBracketOrder.helper'
import type { GroupPositionMap } from './knockoutBracketOrder.helper'

/** Knockout stages in bracket order (group rounds and third place excluded here). */
const KNOCKOUT_ORDER: TournamentStageKey[] = [
  'round_of_32',
  'round_of_16',
  'quarter_finals',
  'semi_finals',
  'final',
]

const ROUND_LABELS: Partial<Record<TournamentStageKey, string>> = {
  round_of_32: 'Round of 32',
  round_of_16: 'Round of 16',
  quarter_finals: 'Quarter-finals',
  semi_finals: 'Semi-finals',
  final: 'Final',
}

function formatBracketDate(kickoffTime: string): string {
  const d = new Date(kickoffTime)
  if (Number.isNaN(d.getTime())) return ''
  // Compact "Jun 29" to fit the small bracket box.
  const day = new Intl.DateTimeFormat('en', { day: 'numeric' }).format(d)
  const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(d)
  return `${month} ${day}`
}

/** Final score for display, including extra time (`homeScore`/`awayScore`). */
function sideScore(value: number | undefined): number | null {
  return typeof value === 'number' ? value : null
}

function winnerOf(m: WorldCupMatch): 'home' | 'away' | null {
  if (m.status !== 'finished') return null
  const h = m.homeScore
  const a = m.awayScore
  if (typeof h !== 'number' || typeof a !== 'number') return null
  if (h > a) return 'home'
  if (a > h) return 'away'
  // Level after the score line — decided on penalties when present.
  const ph = m.penaltyHomeScore
  const pa = m.penaltyAwayScore
  if (typeof ph === 'number' && typeof pa === 'number') {
    if (ph > pa) return 'home'
    if (pa > ph) return 'away'
  }
  return null
}

function toSlot(
  name: string,
  countryCode: string,
  score: number | null,
  penalty: number | null,
  isWinner: boolean | null,
): BracketTeamSlot {
  // A real entrant has a country code; otherwise the API string is a placeholder
  // label (e.g. "Winner Group A") shown against a dashed crest.
  const resolved = countryCode != null && countryCode !== ''
  if (!resolved) {
    return { name: null, placeholderLabel: name || 'To be decided' }
  }
  return { name, countryCode, score, penaltyScore: penalty, isWinner }
}

function toBracketMatch(m: WorldCupMatch): BracketMatch {
  const winner = winnerOf(m)
  return {
    id: m.id,
    home: toSlot(
      m.homeTeam,
      m.homeCountryCode,
      sideScore(m.homeScore),
      typeof m.penaltyHomeScore === 'number' ? m.penaltyHomeScore : null,
      winner == null ? null : winner === 'home',
    ),
    away: toSlot(
      m.awayTeam,
      m.awayCountryCode,
      sideScore(m.awayScore),
      typeof m.penaltyAwayScore === 'number' ? m.penaltyAwayScore : null,
      winner == null ? null : winner === 'away',
    ),
    dateLabel: formatBracketDate(m.kickoffTime),
    timeLabel: formatKickoffClock(m.kickoffTime),
    status: m.status,
    isFinal: m.stageKey === 'final',
    href: `/app/worldcup/match/${m.id}`,
  }
}

function byKickoff(a: WorldCupMatch, b: WorldCupMatch): number {
  return new Date(a.kickoffTime).getTime() - new Date(b.kickoffTime).getTime()
}

/**
 * Build the knockout rounds (outer → inner) and third-place tie from the feed, in KICKOFF order —
 * no bracket-slot ordering applied. The tab slots these via `computeKnockoutSlotsByKey` so it can
 * overlay real ties onto the skeleton per-slot (and show partial rounds); `buildWorldCupBracket`
 * applies slot ordering for the standalone real-only model.
 */
export function buildWorldCupBracketRounds(
  matches: readonly WorldCupMatch[],
): { rounds: BracketRound[]; thirdPlace: BracketMatch | null } {
  const rounds: BracketRound[] = []

  for (const stageKey of KNOCKOUT_ORDER) {
    const stageMatches = matches
      .filter((m) => m.stageKey === stageKey)
      .slice()
      .sort(byKickoff)
    if (stageMatches.length === 0) continue
    rounds.push({
      key: stageKey,
      label: ROUND_LABELS[stageKey] ?? stageMatches[0].stageLabel,
      matches: stageMatches.map(toBracketMatch),
    })
  }

  const thirdPlaceMatch = matches.find((m) => m.stageKey === 'third_place')

  return {
    rounds,
    thirdPlace: thirdPlaceMatch != null ? toBracketMatch(thirdPlaceMatch) : null,
  }
}

/**
 * Standalone real-only bracket model, rounds reordered into true bracket-slot order.
 *
 * @param matches            knockout fixtures from the matches feed.
 * @param groupPositionMap   `countryCode → { groupLetter, position }` from the tournament
 *                           standings. Used to anchor the Round of 32 to its bracket slots; when
 *                           omitted/empty the rounds keep kickoff order (no regression).
 */
export function buildWorldCupBracket(
  matches: readonly WorldCupMatch[],
  groupPositionMap: GroupPositionMap = {},
): KnockoutBracketModel {
  const { rounds, thirdPlace } = buildWorldCupBracketRounds(matches)
  return {
    rounds: orderKnockoutRounds(rounds, groupPositionMap),
    thirdPlace,
  }
}

/** True when the competition actually has knockout fixtures to render a bracket for. */
export function hasKnockoutFixtures(matches: readonly WorldCupMatch[]): boolean {
  return matches.some((m) => KNOCKOUT_ORDER.includes(m.stageKey) || m.stageKey === 'third_place')
}
